import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CartItemPriceDisplay from '@components/cart/CartItemPriceDisplay';
import CartItemRow from '@components/cart/CartItemRow';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { CurrencyObj } from '@contracts';

interface Props {
  discountValue: number;
  currency: CurrencyObj;
  showDiscountValue: boolean;
}

const DiscountCartItemRow: React.FC<Props> = ({ discountValue, currency, showDiscountValue }) => {
  const { t } = useTranslation();
  const context = useContext(Context);
  const { state } = context;
  const { coupon } = state;

  const getDiscountAmountText = () => {
    if (!coupon) return '';
    if (coupon.discount.type === 'percent') {
      return `${coupon.discount.percent}%`;
    }
    const discountForCurrency = coupon.discount.currencies.find((a: any) => a.currency === currency.letterCode);
    const discountAmount = discountForCurrency?.amount ?? coupon.discount.currencies[0].amount;
    return `${currency.symbol}${discountAmount.toFixed(2)}`;
  };

  // Show discount row only if there's an actual discount value > 0
  if (discountValue <= 0) return null;

  // Only show negative price if there's actually a discount amount
  const priceText = discountValue > 0 ? `-${currency.symbol}${discountValue?.toFixed(2)}` : '';

  const imageComponent = <DiscountImageWrap>%</DiscountImageWrap>;

  const titleComponent = (
    <>
      {coupon && <span>{t('components.discount-amount', { amount: getDiscountAmountText() })}</span>}
      {!coupon && showDiscountValue && <span>{t('components.discount')}</span>}
    </>
  );

  const subtitleComponent = coupon?.code ? <>{t('components.discount-code', { code: coupon.code.toUpperCase() })}</> : undefined;

  const priceComponent = priceText ? <CartItemPriceDisplay price={priceText} textColor={Colors.navyBlue} /> : null;

  return (
    <CartItemRow
      imageComponent={imageComponent}
      titleComponent={titleComponent}
      subtitleComponent={subtitleComponent}
      priceComponent={priceComponent}
    />
  );
};

export default DiscountCartItemRow;

const DiscountImageWrap = styled.div`
  width: 40px;
  min-width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 15px;
  background: ${Colors.gold};
  color: ${Colors.cream};
  text-align: center;
  line-height: 40px;
  font-size: 20px;
  font-family: 'BrownStd';
`;
